import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CountryProvider } from "@/contexts/CountryContext";
import { LocalityProvider } from "@/contexts/LocalityContext";
import { Header } from "@/components/layout/Header";
import SisterSitesBar from "@/components/SisterSitesBar";
import CategoryRedirect from "@/pages/CategoryRedirect";
import CemeteriesDirectory from "@/pages/CemeteriesDirectory";
import CemeteryDetail from "@/pages/CemeteryDetail";
import { FuneralServicesDirectory, FuneralServiceDetail } from "@/pages/FuneralServices";
import { Footer } from "@/components/layout/Footer";
import CookieBanner from "@/components/CookieBanner";
import { QuickReporterButton } from "@/components/QuickReporter";
import { installAnalyticsConsentHook } from "@/lib/analytics";
import { TaglineBanner } from "@/components/layout/TaglineBanner";
import { LocationHeroStrip } from "@/components/layout/LocationHeroStrip";
import LocalityHeader from "@/components/LocalityHeader";
import NewsPulseApp from "@/pages/newspulse/NewsPulseApp";
import { resolveSiteSlug, redirectForHost } from "@/config/siteConfig";
import ExploreBar from "@/components/ExploreBar";
import PageTranslateBar from "@/components/PageTranslateBarV2";
import PageTTSButton from "@/components/PageTTSButton";
import GlobalInputTranslator from "@/components/GlobalInputTranslator";
import ScrollToContentOnRoute from "@/components/ScrollToContentOnRoute";
import VerificationBanner from "@/components/VerificationBanner";
import IdleLogout from "@/components/IdleLogout";
import DomSpellingLocaliser from "@/components/DomSpellingLocaliser";
import { AIHelper } from "@/components/AIHelper";
import ReportContributionFAB from "@/components/ReportContributionFAB";
import { Toaster } from "@/components/ui/sonner";
import CountryAutoDetect from "@/components/CountryAutoDetect";
import LanguageAutoSuggest from "@/components/LanguageAutoSuggest";
import FontScaleSwitcher from "@/components/FontScaleSwitcher";
import Home from "@/pages/Home";
import HomeRouter from "@/pages/HomeRouter";
import AdvertisePage from "@/pages/AdvertisePage";
import BlogHome from "@/pages/blog/BlogHome";
import { BlogNews, BlogPlaces, BlogTopics, BlogPublications, BlogWriters } from "@/pages/blog/BlogSubpages";
import BlogTopicSuggest from "@/pages/blog/BlogTopicSuggest";
import BlogAuth from "@/pages/blog/BlogAuth";
import BlogQuestions from "@/pages/blog/BlogQuestions";
import BlogTopicHub from "@/pages/blog/BlogTopicHub";
// ============ Food Pulse Mobile Web App ============
import MobileShell from "@/mobile/MobileShell";
import MobileSplash from "@/mobile/MobileSplash";
import MobileOnboarding from "@/mobile/MobileOnboarding";
import MobileHome from "@/mobile/MobileHome";
import MobileDeals from "@/mobile/MobileDeals";
import MobileSettings from "@/mobile/MobileSettings";
import MobileProfile from "@/mobile/MobileProfile";
import MobileLogin from "@/mobile/MobileLogin";
import MobileProducts from "@/mobile/MobileProducts";
import MobileProductDetail from "@/mobile/MobileProductDetail";
import MobileStores from "@/mobile/MobileStores";
import MobileCharities from "@/mobile/MobileCharities";
import MobileMyLists from "@/mobile/MobileMyLists";
import MobileAddRecipe from "@/mobile/MobileAddRecipe";
import MobileAddDeal from "@/mobile/MobileAddDeal";
import MobileRecipes from "@/mobile/MobileRecipes";
import MobileRecipeDetail from "@/mobile/MobileRecipeDetail";
import {
  MobileNews, MobileMyDeals, MobileGroups, MobileAbout,
} from "@/mobile/MobileStubs";
import BusinessPortalHome from "@/pages/BusinessPortalHome";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import BusinessEditPage from "@/pages/BusinessEditPage";
import BusinessLocationPage from "@/pages/BusinessLocationPage";
import NewBusinessPage from "@/pages/NewBusinessPage";
import ProductEditPage from "@/pages/ProductEditPage";
import CartPage from "@/pages/CartPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import OrdersPage from "@/pages/OrdersPage";
import JobsHome from "@/pages/JobsHome";
import JobsPostPage from "@/pages/JobsPostPage";
import JobDetailPage from "@/pages/JobDetailPage";
import JobsDashboardPage from "@/pages/JobsDashboardPage";
import RealEstateHome from "@/pages/RealEstateHome";
import RealEstatePostPage from "@/pages/RealEstatePostPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import TravelHome from "@/pages/TravelHome";
import TravelListPage from "@/pages/TravelListPage";
import TravelDetailPage from "@/pages/TravelDetailPage";
import TravelOperatorNew from "@/pages/TravelOperatorNew";
import QuoteDetailPage from "@/pages/QuoteDetailPage";
import QuoteAuthorPage from "@/pages/QuoteAuthorPage";
import QuoteSubmitPage from "@/pages/QuoteSubmitPage";
import QuoteAdminImport from "@/pages/QuoteAdminImport";
import TravelAdminGenerate from "@/pages/TravelAdminGenerate";
import AdminPhotosReview from "@/pages/AdminPhotosReview";
import AdminAdsManager from "@/pages/AdminAdsManager";
import AdminModerationFeedback from "@/pages/AdminModerationFeedback";
import StaffMagicLinkConsume from "@/pages/StaffMagicLinkConsume";
import SportsHome from "@/pages/SportsHome";
import SportsCategoryPage from "@/pages/SportsCategoryPage";
import SportsTournamentsList from "@/pages/SportsTournamentsList";
import SportsTournamentNew from "@/pages/SportsTournamentNew";
import SportsTournamentDetail from "@/pages/SportsTournamentDetail";
import SportsClubsList from "@/pages/SportsClubsList";
import SportsClubNew from "@/pages/SportsClubNew";
import SportsClubDetail from "@/pages/SportsClubDetail";
import EducationHome from "@/pages/EducationHome";
import EducationInstitutionsList from "@/pages/EducationInstitutionsList";
import EducationInstitutionNew from "@/pages/EducationInstitutionNew";
import EducationInstitutionDetail from "@/pages/EducationInstitutionDetail";
import EducationScholarshipsList from "@/pages/EducationScholarshipsList";
import EducationScholarshipNew from "@/pages/EducationScholarshipNew";
import EducationScholarshipDetail from "@/pages/EducationScholarshipDetail";
import WebsiteWizard from "@/pages/WebsiteWizard";
import MyWebsitesPage from "@/pages/MyWebsitesPage";
import WebsiteEditPage from "@/pages/WebsiteEditPage";
import PublicWebsitePage from "@/pages/PublicWebsitePage";
import GovernmentHome from "@/pages/GovernmentHome";
import GovernmentDepartmentsList from "@/pages/GovernmentDepartmentsList";
import GovernmentDepartmentNew from "@/pages/GovernmentDepartmentNew";
import GovernmentDepartmentDetail from "@/pages/GovernmentDepartmentDetail";
import GovernmentNoticesList from "@/pages/GovernmentNoticesList";
import MyGovernmentMessagesPage from "@/pages/MyGovernmentMessagesPage";
// Health, Wellness & Medicine vertical
import HealthHome from "@/pages/HealthHome";
import HealthFacilitiesList from "@/pages/HealthFacilitiesList";
import HealthFacilityDetail from "@/pages/HealthFacilityDetail";
import HealthProvidersList from "@/pages/HealthProvidersList";
import HealthProviderDetail from "@/pages/HealthProviderDetail";
import HealthTopicsList from "@/pages/HealthTopicsList";
// Arts, Music & Cinema vertical
import ArtsHome from "@/pages/ArtsHome";
import ArtsSection from "@/pages/ArtsSection";
import ArtsProfile from "@/pages/ArtsProfile";
import ArtsSubmit from "@/pages/ArtsSubmit";
import ArtsAwardsList from "@/pages/ArtsAwardsList";
import ArtsAwardDetail from "@/pages/ArtsAwardDetail";
import ArtsMarketplace from "@/pages/ArtsMarketplace";
import HealthTopicDetail from "@/pages/HealthTopicDetail";
// Global Knowledge Graph
import KgEntityDetail from "@/pages/KgEntityDetail";
import ExplorePlaces from "@/pages/ExplorePlaces";
import FindHelp from "@/pages/FindHelp";
import OfferHelp from "@/pages/OfferHelp";
import FindOfferHelp from "@/pages/FindOfferHelp";
import ArticlesList from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import ArticleCompose from "@/pages/ArticleCompose";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicProfile from "@/pages/PublicProfile";
import ProfileEdit from "@/pages/ProfileEdit";
import Nature from "@/pages/Nature";
import NatureDiscover from "@/pages/NatureDiscover";
import NatureHome from "@/pages/NatureHome";
import NatureWelcome from "@/pages/NatureWelcome";
import NatureAtlasCategory from "@/pages/NatureAtlasCategory";
import NatureSpeciesProfile from "@/pages/NatureSpeciesProfile";
import PlacesDirectory from "@/pages/PlacesDirectory";
import PoiDetail from "@/pages/PoiDetail";
import LocationTopicPage from "@/pages/LocationTopicPage";
import NatureIdentify from "@/pages/NatureIdentify";
import NatureObservations from "@/pages/NatureObservations";
import NatureFromTheFounder from "@/pages/NatureFromTheFounder";
import SellnBuyHome from "@/pages/SellnBuyHome";
import LocalListingDetail from "@/pages/LocalListingDetail";
import LocalCategoryBrowse from "@/pages/LocalCategoryBrowse";
import SocialCapitalHome from "@/pages/SocialCapitalHome";
import SocialCapitalPassport from "@/pages/SocialCapitalPassport";
import SocialCapitalOrgDashboard from "@/pages/SocialCapitalOrgDashboard";
import SocialCapitalApiDocs from "@/pages/SocialCapitalApiDocs";
import SocialCapitalArticle from "@/pages/SocialCapitalArticle";
import MemberProfilePage from "@/pages/MemberProfilePage";
import Civic from "@/pages/Civic";
import NeighborhoodPulse from "@/pages/NeighborhoodPulse";
import NeighborhoodDetail from "@/pages/NeighborhoodDetail";
import PulseItemDetail from "@/pages/PulseItemDetail";
import MyContent from "@/pages/MyContent";
import AppealDetail from "@/pages/AppealDetail";
import Organizations from "@/pages/Organizations";
import OrganizationsCompare from "@/pages/OrganizationsCompare";
import Members from "@/pages/Members";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import LocationArticle from "@/pages/LocationArticle";
import LinkReferencePage from "@/pages/LinkReferencePage";
import GuidesConsole from "@/pages/GuidesConsole";
import StampGallery from "@/pages/StampGallery";
import BookmarksDashboard from "@/pages/BookmarksDashboard";
import KindDirectoryPage from "@/pages/KindDirectoryPage";
import KeralaHome from "@/pages/KeralaHome";
import KeralaTravelsHome from "@/pages/KeralaTravelsHome";
import WhatIsFoodPulse from "@/pages/WhatIsFoodPulse";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ResumeStudio from "@/pages/ResumeStudio";
import ResumeBuilder from "@/pages/ResumeBuilder";
import PublicResumePage from "@/pages/PublicResumePage";
import WorldPulseHome from "@/pages/WorldPulseHome";
import LocationPortalPage from "@/pages/LocationPortalPage";
import MemberProfile from "@/pages/MemberProfile";
import MemberEdit from "@/pages/MemberEdit";
import OrganizationDetail from "@/pages/OrganizationDetail";
import Housing from "@/pages/Housing";
import HabitatForHumanity from "@/pages/HabitatForHumanity";
import HousingDetail from "@/pages/HousingDetail";
import Opportunities from "@/pages/Opportunities";
import VolunteerHome from "@/pages/VolunteerHome";
import VolunteerBenefit from "@/pages/VolunteerBenefit";
import Stories from "@/pages/Stories";
import ChangeMakers from "@/pages/ChangeMakers";
import WorldChangers from "@/pages/WorldChangers";
import WorldChangerDetail from "@/pages/WorldChangerDetail";
import ChangeMakerDetail from "@/pages/ChangeMakerDetail";
import Dashboard from "@/pages/Dashboard";
import MyProfile from "@/pages/MyProfile";
import ShoppingList from "@/pages/ShoppingList";
import Services from "@/pages/Services";
import ServiceSubmit from "@/pages/ServiceSubmit";
import ServiceDetail from "@/pages/ServiceDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import WeatherAlmanac from "@/pages/WeatherAlmanac";
import WeatherAlmanacAbout from "@/pages/WeatherAlmanacAbout";
import WeatherTopicPage from "@/pages/WeatherTopicPage";
import GenericTopicPage from "@/pages/GenericTopicPage";
import WeatherCommunity from "@/pages/WeatherCommunity";
import WeatherReportDetail from "@/pages/WeatherReportDetail";
import FarmerDashboard from "@/pages/FarmerDashboard";
import Marketplace from "@/pages/Marketplace";
import MarketplaceNew from "@/pages/MarketplaceNew";
import MarketplaceDetail from "@/pages/MarketplaceDetail";
import RequestHelp from "@/pages/RequestHelp";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminContactInbox from "@/pages/AdminContactInbox";
import AdminContactFormInbox from "@/pages/AdminContactFormInbox";
import AdminObituaryImport from "@/pages/AdminObituaryImport";
import AdminArticleEditor from "@/pages/AdminArticleEditor";
import AdminArticleList from "@/pages/AdminArticleList";
import AdminAiTopicPhotos from "@/pages/AdminAiTopicPhotos";
import AdminSweepProgress from "@/pages/AdminSweepProgress";
import ExploreIndiaLanding from "@/pages/ExploreIndiaLanding";
import ExploreUsaLanding from "@/pages/ExploreUsaLanding";
import AdminLocationEditor from "@/pages/AdminLocationEditor";
import AdminSatellites from "@/pages/AdminSatellites";
import DailyPulse from "@/pages/DailyPulse";
import StoryDetail from "@/pages/StoryDetail";
import CategoryArticle from "@/pages/CategoryArticle";
import VisionMission from "@/pages/VisionMission";
import SocialCapitalPage from "@/pages/SocialCapitalPage";
import ImpactMap from "@/pages/ImpactMap";
import Contact from "@/pages/Contact";
import Donate from "@/pages/Donate";
import { DonateSuccess, DonateCancel } from "@/pages/DonateResult";
import Pricing from "@/pages/Pricing";
import PremiumSuccess from "@/pages/PremiumSuccess";
import PremiumCancel from "@/pages/PremiumCancel";
import Causes from "@/pages/Causes";
import CauseDetail from "@/pages/CauseDetail";
import SubmitCause from "@/pages/SubmitCause";
import Celebrate from "@/pages/Celebrate";
import CelebrateBrowse from "@/pages/CelebrateBrowse";
import Obituaries from "@/pages/Obituaries";
import ObituariesHub from "@/pages/ObituariesHub";
import ObituariesCategory from "@/pages/ObituariesCategory";
import ObituariesSubmit from "@/pages/ObituariesSubmit";
import PricingIft, { PricingSuccess, PricingCancel } from "@/pages/PricingIft";
import PricingDispatcher from "@/pages/PricingDispatcher";
import FamilyTreeGuide from "@/pages/FamilyTreeGuide";
import CelebrateDetail from "@/pages/CelebrateDetail";
import CelebrateSubmit from "@/pages/CelebrateSubmit";
import CelebrateLinkExisting from "@/pages/CelebrateLinkExisting";
import CelebrateAnnouncementPrint from "@/pages/CelebrateAnnouncementPrint";
import ThingsToKnow from "@/pages/ThingsToKnow";
import ThingsToKnowDetail from "@/pages/ThingsToKnowDetail";
import ThingsToKnowArchive from "@/pages/ThingsToKnowArchive";
import NewsSubmit from "@/pages/NewsSubmit";
import SearchResults from "@/pages/SearchResults";
import FamilyTreesList from "@/pages/FamilyTrees";
import FamilyTreeNew from "@/pages/FamilyTreeNew";
import FamilyTreeDetail from "@/pages/FamilyTreeDetail";
import FamilyMemorialsMarketing from "@/pages/FamilyMemorialsMarketing";
import HeritageHome from "@/pages/HeritageHome";
import GlobalFamilyTrees from "@/pages/GlobalFamilyTrees";
import AdminNetworkLaunch from "@/pages/AdminNetworkLaunch";
import FamilyManagementCenter from "@/pages/FamilyManagementCenter";
import FamilyPhotosBatch from "@/pages/FamilyPhotosBatch";
import AlmanacRecordDetail from "@/pages/AlmanacRecordDetail";
import AlmanacAdminNew from "@/pages/AlmanacAdminNew";
import AlmanacMap from "@/pages/AlmanacMap";
import FamilyBookViewer from "@/pages/FamilyBookViewer";
import IndianFamilyTreeStatePage from "@/pages/IndianFamilyTreeStatePage";
import IndianFamilyTreeHome from "@/pages/IndianFamilyTreeHome";
import KinNetwork from "@/pages/KinNetwork";
import PersonProfile from "@/pages/PersonProfile";
import ReunionMixer from "@/pages/ReunionMixer";
import Recipes from "@/pages/Recipes";
import RecipeDetail from "@/pages/RecipeDetail";
import RecipeSubmit from "@/pages/RecipeSubmit";
import RecipePrint from "@/pages/RecipePrint";
import InMemoriam from "@/pages/InMemoriam";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import EventSubmit from "@/pages/EventSubmit";
import PolicyPage from "@/pages/PolicyPage";
import IftFamousFamilies from "@/pages/IftFamousFamilies";
import IftIndianHeritage from "@/pages/IftIndianHeritage";
import HeritageArticlePage from "@/pages/HeritageArticlePage";
import UnsplashComplianceDemo from "@/pages/UnsplashComplianceDemo";
import IftExploreIndia from "@/pages/IftExploreIndia";
import IftAboutPage from "@/pages/IftAboutPage";
import SocialPulseAbout from "@/pages/SocialPulseAbout";
import SocialPulseContact from "@/pages/SocialPulseContact";
import SocialPulseExplore from "@/pages/SocialPulseExplore";
import SocialPulseFounder from "@/pages/SocialPulseFounder";
import SocialPulseHelp from "@/pages/SocialPulseHelp";
import SocialPulseProfile from "@/pages/SocialPulseProfile";
import SocialPulseProfileOnboard from "@/pages/SocialPulseProfileOnboard";
import SocialPulsePlaces from "@/pages/SocialPulsePlaces";
import PulseArticle from "@/pages/PulseArticle";
import AdminContentEngine from "@/pages/AdminContentEngine";
import AdminContentEngineReport from "@/pages/AdminContentEngineReport";
import AdminSisters from "@/pages/AdminSisters";
import AdminAds from "@/pages/AdminAds";
import AdminAdCampaigns from "@/pages/AdminAdCampaigns";
import AdminAdCampaignAnalytics from "@/pages/AdminAdCampaignAnalytics";
import AdBuilder from "@/pages/AdBuilder";
import ContentCmsShortcut from "@/components/socialpulse/ContentCmsShortcut";
import SocialPulseJoin from "@/pages/SocialPulseJoin";
import {
  SocialPulsePress,
  SocialPulseCareers,
  SocialPulseVisionMission,
  SocialPulsePrivacy,
  SocialPulseCommunityGuidelines,
  SocialPulseTopics,
  SocialPulseNotFound as SocialPulseNotFoundPage,
} from "@/pages/SocialPulseSimple";
import { SocialPulseCauses, SocialPulseCommunity } from "@/pages/SocialPulseSubPages";
import SocialPulseTerms from "@/pages/SocialPulseTerms";
import SocialPulseNetwork from "@/pages/SocialPulseNetwork";
import SocialPulseSearch from "@/pages/SocialPulseSearch";
import TopicsIndex from "@/pages/TopicsIndex";
import FoodItemsDirectory from "@/pages/FoodItemsDirectory";
import FoodItemPage from "@/pages/FoodItemPage";
import FoodBusinessesDirectory from "@/pages/FoodBusinessesDirectory";
import FoodBusinessPage from "@/pages/FoodBusinessPage";
import PhotoPulseQueue from "@/pages/publisher/PhotoPulseQueue";

import FoodPulseHome from "@/pages/FoodPulseHome";
import PublisherWrite from "@/pages/publisher/PublisherWrite";
import PublisherDashboard from "@/pages/publisher/PublisherDashboard";
import WriterProfile from "@/pages/publisher/WriterProfile";
import PublisherHub from "@/pages/publisher/PublisherHub";
import PublisherAdmin from "@/pages/publisher/PublisherAdmin";
import PublisherNotesFeed from "@/pages/publisher/PublisherNotesFeed";
import PublisherArticleView from "@/pages/publisher/PublisherArticleView";
import ChatPulseHome from "@/pages/chatpulse/ChatPulseHome";
import FoodPulseProductsDirectory from "@/pages/FoodPulseProductsDirectory";
import FoodPulseProductDetail from "@/pages/FoodPulseProductDetail";
import FoodPulseUrbanHarvest from "@/pages/FoodPulseUrbanHarvest";
import FoodPulseUrbanHarvestStory from "@/pages/FoodPulseUrbanHarvestStory";
import FoodPulseUrbanHarvestPickYourOwn from "@/pages/FoodPulseUrbanHarvestPickYourOwn";
import FoodPulseUrbanHarvestPrePickedCurbside from "@/pages/FoodPulseUrbanHarvestPrePickedCurbside";
import FoodPulseUrbanHarvestDonationOnly from "@/pages/FoodPulseUrbanHarvestDonationOnly";
import FoodPulseUrbanHarvestHarvestHelp from "@/pages/FoodPulseUrbanHarvestHarvestHelp";
import FoodPulseArticles from "@/pages/FoodPulseArticles";
import FoodPulseArticlePage from "@/pages/FoodPulseArticlePage";
import FoodPulseSection from "@/pages/FoodPulseSection";
import FoodPulseRecipesCategory from "@/pages/FoodPulseRecipesCategory";
import FoodPulseHealth from "@/pages/FoodPulseHealth";
import FoodPulseAbout from "@/pages/FoodPulseAbout";
import FoodPulseTerms from "@/pages/FoodPulseTerms";
import IndiaPrivacyPolicy from "@/pages/legal/IndiaPrivacyPolicy";
import NomineeSettings from "@/pages/NomineeSettings";
import NomineeAccept from "@/pages/NomineeAccept";
import FoodPulseAdmin from "@/pages/admin/FoodPulseAdmin";
import FoodPulseDealsGrid from "@/pages/FoodPulseDealsGrid";
import FoodPulseDealDetail from "@/pages/FoodPulseDealDetail";
import VendorDashboard from "@/pages/VendorDashboard";
import MyFoodLists from "@/pages/MyFoodLists";
import AddWishProductForm from "@/pages/AddWishProductForm";
import ReceiptScanner from "@/pages/ReceiptScanner";
import MyReceipts from "@/pages/MyReceipts";
import HouseholdDashboard from "@/pages/HouseholdDashboard";
import SmartBasket from "@/pages/SmartBasket";
import SpotADeal from "@/pages/SpotADeal";
import HarvestBoard from "@/pages/HarvestBoard";
import FarmersMarkets from "@/pages/FarmersMarkets";
import FarmstandPage from "@/pages/FarmstandPage";
import FarmProfileEditor from "@/pages/FarmProfileEditor";
import IngredientEncyclopedia from "@/pages/IngredientEncyclopedia";
import IngredientDetail from "@/pages/IngredientDetail";
import CsvDealImport from "@/pages/CsvDealImport";
import FoodPulseLabelScan from "@/pages/FoodPulseLabelScan";
import FoodPulseTopicLanding from "@/pages/FoodPulseTopicLanding";
import AdminFoodItems from "@/pages/admin/AdminFoodItems";
import MeetTheFounder from "@/pages/MeetTheFounder";
import NotFound from "@/pages/NotFound";
import SocialPulseCallback from "@/pages/SocialPulseCallback";
import ServicesHomePage from "@/pages/services/ServicesHomePage";
import RequestServicePage from "@/pages/services/RequestServicePage";
import RFSDetailPage from "@/pages/services/RFSDetailPage";
import ProviderDashboardPage from "@/pages/services/ProviderDashboardPage";
import IntelligenceAdminPage from "@/pages/admin/IntelligenceAdminPage";
import AdminSsoRolloutPage from "@/pages/admin/AdminSsoRolloutPage";
import BlogLeaderboard from "@/pages/blog/BlogLeaderboard";
import AdminEmailOutbox from "@/pages/admin/AdminEmailOutbox";
import AdminSocialCapitalLedger from "@/pages/admin/AdminSocialCapitalLedger";
import CivicHome from "@/pages/civic/CivicHome";
import CivicReport from "@/pages/civic/CivicReport";
import CivicMapPage from "@/pages/civic/CivicMapPage";
import CivicIssueDetail from "@/pages/civic/CivicIssueDetail";
import { CivicResolved, CivicCommunities, CivicAuthorities } from "@/pages/civic/CivicPlaceholders";
import { CivicAuthorityDirectory, CivicAuthorityInbox } from "@/pages/civic/CivicAuthorityInbox";
import { CivicHeader, CivicFooter } from "@/components/civic/CivicChrome";
import { useEffect } from "react";
import { useLocation as useLocRR } from "react-router-dom";

/** Legacy redirect: /publisher/@:authorId → /publisher/author/:authorId */
function PublisherHubLegacyRedirect() {
  const loc = useLocRR();
  // Path looks like /publisher/@<id>[/anything]
  const m = loc.pathname.match(/^\/publisher\/@([^/]+)/);
  const authorId = m ? decodeURIComponent(m[1]) : null;
  if (!authorId) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="publisher-unknown">
        Unknown Publisher URL.
      </div>
    );
  }
  return <Navigate to={`/publisher/author/${authorId}`} replace />;
}

/** SearchDispatcher — route /search to the SocialPulse-branded search
 *  when the visitor is on socialpulse.com (or without a child-site
 *  override); otherwise fall through to the legacy SearchResults page
 *  so sister sites keep their own chrome. */
function SearchDispatcher() {
  try {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const site = (params.get("site") || "").toLowerCase();
    if (site === "socialpulse") return <SocialPulseSearch />;
    const KNOWN_SISTER_SLUGS = new Set([
      "kerala", "keralatravels", "indianfamilytree", "myfamilytreecare",
      "foodpulse", "sellnbuy", "socialcapital", "worldpulse", "indias",
      "usapulse", "blogspulse", "blog", "newspulse", "civicpulse",
    ]);
    if (site && KNOWN_SISTER_SLUGS.has(site)) return <SearchResults />;
    const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    if (!host) return <SocialPulseSearch />;
    if (host.includes("indianfamilytree.com")) return <SearchResults />;
    if (host.includes("myfamilytreecare.com")) return <SearchResults />;
    if (host.includes("familytree.team"))     return <SearchResults />;
    if (host.includes("keralatravels"))       return <SearchResults />;
    if (host.includes("kerala.com"))          return <SearchResults />;
    if (host.includes("foodpulse"))           return <SearchResults />;
    if (host.includes("civicpulse.blog"))     return <SearchResults />;
    if (host.includes("socialpulse.blog"))    return <SearchResults />;
    return <SocialPulseSearch />;
  } catch {
    return <SocialPulseSearch />;
  }
}

/** HelpDispatcher — route /help to the SocialPulse-branded Help Centre
 *  when on socialpulse.com; otherwise fall through to the legacy
 *  Find / Offer Help hub used by sister sites. */
function HelpDispatcher() {
  try {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const site = (params.get("site") || "").toLowerCase();
    if (site === "socialpulse") return <SocialPulseHelp />;
    const KNOWN_SISTER_SLUGS = new Set([
      "kerala", "keralatravels", "indianfamilytree", "myfamilytreecare",
      "foodpulse", "sellnbuy", "socialcapital", "worldpulse", "indias",
      "usapulse", "blogspulse", "blog", "newspulse", "civicpulse",
    ]);
    if (site && KNOWN_SISTER_SLUGS.has(site)) return <FindOfferHelp />;
    const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    if (!host) return <SocialPulseHelp />;
    if (host.includes("indianfamilytree.com")) return <FindOfferHelp />;
    if (host.includes("myfamilytreecare.com")) return <FindOfferHelp />;
    if (host.includes("familytree.team"))     return <FindOfferHelp />;
    if (host.includes("keralatravels"))       return <FindOfferHelp />;
    if (host.includes("kerala.com"))          return <FindOfferHelp />;
    if (host.includes("foodpulse"))           return <FindOfferHelp />;
    if (host.includes("civicpulse.blog"))     return <FindOfferHelp />;
    if (host.includes("socialpulse.blog"))    return <FindOfferHelp />;
    return <SocialPulseHelp />;
  } catch {
    return <SocialPulseHelp />;
  }
}

/** ExploreDispatcher — route /explore to the SocialPulse-branded Explore
 *  hub on socialpulse.com; otherwise fall through to the legacy
 *  ExplorePlaces page used by sister sites. */
function ExploreDispatcher() {
  return _spDispatch("explore", <SocialPulseExplore />, <ExplorePlaces />);
}

/** Shared site-slug detector: returns true when the current visit is
 *  a socialpulse.com surface (or the default preview host), false when
 *  it's a known sister site (kerala.com, foodpulse, etc.). Used by all
 *  the sister-site-aware dispatchers below to avoid duplicating the
 *  slug-map at every route. */
function _isSocialPulseHost() {
  try {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const site = (params.get("site") || "").toLowerCase();
    if (site === "socialpulse") return true;
    const KNOWN_SISTER_SLUGS = new Set([
      "kerala", "keralatravels", "indianfamilytree", "myfamilytreecare",
      "foodpulse", "sellnbuy", "socialcapital", "worldpulse", "indias",
      "usapulse", "blogspulse", "blog", "newspulse", "civicpulse",
    ]);
    if (site && KNOWN_SISTER_SLUGS.has(site)) return false;
    const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    if (!host) return true;
    if (host.includes("indianfamilytree.com")) return false;
    if (host.includes("myfamilytreecare.com")) return false;
    if (host.includes("familytree.team"))     return false;
    if (host.includes("keralatravels"))       return false;
    if (host.includes("kerala.com"))          return false;
    if (host.includes("foodpulse"))           return false;
    if (host.includes("civicpulse.blog"))     return false;
    if (host.includes("socialpulse.blog"))    return false;
    return true;
  } catch { return true; }
}
function _spDispatch(_label, sp, sister) { return _isSocialPulseHost() ? sp : sister; }

/** Contact — SP-only for now. */
function ContactDispatcher() { return <SocialPulseContact />; }
/** Places — SP hub or legacy PlacesDirectory. */
function PlacesDispatcher() { return _spDispatch("places", <SocialPulsePlaces />, <PlacesDirectory />); }
/** Causes — SP hub or legacy Causes. */
function CausesDispatcher() { return _spDispatch("causes", <SocialPulseCauses />, <Causes />); }
/** Community — SP only (no legacy route). */
function CommunityDispatcher() { return <SocialPulseCommunity />; }


function App() {
  useEffect(() => {
    installAnalyticsConsentHook();
  }, []);

  // Fork redirect gate — runs BEFORE any React tree renders. If the
  // current host has a live fork (thenewspulse.pro / chatspulse.pro),
  // hard-redirect once we're in the browser. `HOST_REDIRECTS` in
  // siteConfig.js is intentionally empty until each fork is verified
  // live, so this is a no-op today. Flip an entry there to activate.
  if (typeof window !== "undefined") {
    try {
      const target = redirectForHost(window.location.hostname, window.location.pathname + window.location.search);
      if (target) {
        window.location.replace(target);
        return null;
      }
    } catch { /* fail-open — never block render on redirect logic */ }
  }

  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <CountryProvider>
          <LocalityProvider>
          <AppShell />
          </LocalityProvider>
          </CountryProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Route-aware chrome wrapper.  Hides the decorative pre-content banners
// (locality header, tagline hero, explore chip strip, translate bar) on
// auth flow pages so users don't have to scroll past ~1000px of chrome
// to see the login form on mobile.  The Header (with mobile Sign in /
// Join CTAs) and Footer stay visible so users can still navigate.
function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAuthPage = /^\/(login|register|forgot-password|reset-password|verify-email)\b/.test(pathname);
  const isHomePage = pathname === "/";
  // Mobile-web app shell (/m/*) owns the entire viewport — hide all
  // Social Pulse chrome. Splash + onboarding also render outside the
  // MobileShell layout so we treat any /m path as immersive.
  const isMobileApp = /^\/m(\/|$)/.test(pathname);

  // Auto-redirect: on foodpulse.net mother/child site + mobile viewport,
  // steer first-time visitors to the branded mobile web app shell. We
  // Legacy-host 301-equivalent: bookmarks pointing at the old
  // BlogsPulse subdomain (blogs.socialpulse.com) bounce once to the
  // new canonical apex on socialpulse.blog. Same for the ChatsPulse
  // and NewsPulse hosts. Deep-links are preserved. Runs before any
  // other side effects.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hostname;
    const REDIRECTS = {
      "blogs.socialpulse.com": "socialpulse.blog",
      "www.blogs.socialpulse.com": "socialpulse.blog",
      "chats.socialpulse.com": "chats.socialpulse.blog",
      "news.socialpulse.com": "news.socialpulse.blog",
    };
    const target = REDIRECTS[h];
    if (target && target !== h) {
      const url = `https://${target}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(url);
    }
  }, []);

  // OIDC bounce rescuer. The /oauth/authorize endpoint sets a short-
  // lived `sp_oidc_pending` cookie before redirecting an anonymous
  // user to /login. If Firebase's Google sign-in falls back to a FULL
  // redirect (mobile browsers often do), the user comes back on `/`
  // instead of /login, and Login.jsx never mounts to finish the
  // bounce. This watcher runs on every route change — if we have a
  // signed-in Firebase user AND the pending cookie is still set AND
  // we're not already on an /auth or /oauth page, we complete the
  // OIDC bounce by full-navigating to the stored authorize URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (/^\/(auth|oauth|login)\b/.test(pathname)) return;
    const readCookie = (name) => {
      try {
        const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]+)"));
        return m ? decodeURIComponent(m[1]) : null;
      } catch { return null; }
    };
    const pending = readCookie("sp_oidc_pending");
    if (!pending || !pending.startsWith("/api/oauth/authorize")) return;
    // Only bounce if we actually have an auth cookie/token — otherwise
    // the user hasn't completed sign-in yet and the bounce would just
    // loop them back to /login.
    const hasAppToken = !!(() => { try { return localStorage.getItem("sp_token"); } catch { return null; } })();
    if (!hasAppToken) return;
    // Clear the cookie first so a transient failure doesn't trap us
    // in an endless redirect.
    try { document.cookie = "sp_oidc_pending=; Max-Age=0; Path=/; SameSite=Lax"; } catch { /* ignore */ }
    try { sessionStorage.removeItem("sp_oidc_return"); } catch { /* ignore */ }
    window.location.href = pending;
  }, [pathname]);

  // FoodPulse mobile shell — only redirect from the site root ("/") so
  // deep-linked URLs are respected, and we remember the choice via
  // localStorage.
  useEffect(() => {
    if (typeof window === "undefined" || pathname !== "/") return;
    if (localStorage.getItem("fp_skip_mobile_shell") === "1") return;
    const isFoodPulseHost = /foodpulse/i.test(window.location.hostname);
    const isNarrow = window.matchMedia("(max-width: 767px)").matches;
    if (isFoodPulseHost && isNarrow) {
      const done = localStorage.getItem("fp_mobile_onboarded") === "1";
      navigate(done ? "/m" : "/m/splash", { replace: true });
    }
  }, [pathname, navigate]);
  // Hosted-site pages (/sites/<slug>) are meant to feel like the
  // organization's own website — so we hide all Social Pulse chrome
  // (header, banners, footer). The site's own SP-branded footer link
  // is still rendered by PublicWebsitePage.
  const isHostedSite = /^\/sites\//.test(pathname);
  // Immersive routes hide the site chrome so the full viewport belongs
  // to the reader (book viewer, fullscreen media, etc.).
  const isImmersive = /^\/family\/[^/]+\/book\//.test(pathname);
  // The Heritage landing owns its own thin sibling-site nav, so we
  // suppress the fat multi-row Social Pulse chrome on those routes.
  const isHeritageLanding = pathname === "/heritage" || pathname === "/family-and-memorials" || pathname === "/global-family-trees";
  // Publisher writing / admin surfaces should feel like the .blog site
  // — no fat SocialPulse chrome bouncing writers between design systems.
  const isPublisherSurface = /^\/(publisher|writers)(\/|$)/.test(pathname);
  // Preview override: `?site=blog` (or `?site=chats`, etc.) treats the
  // current tab as if it were served from the canonical apex, letting
  // us preview the distraction-free .blog experience on the shared
  // Emergent preview URL before DNS is wired. We also latch the choice
  // into sessionStorage so it survives client-side navigations.
  const isBlogPreview = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const site = params.get("site");
    if (site) {
      try { window.sessionStorage.setItem("sp_preview_site", site); } catch { /* noop */ }
    }
    const latched = (() => {
      try { return window.sessionStorage.getItem("sp_preview_site"); } catch { return null; }
    })();
    return (site || latched) === "blog";
  })();
  const isBlogSurface = isBlogPreview || /^\/blog(\/|$)/.test(pathname);
  // Feb 2026 — new SocialPulseHome renders at "/" on socialpulse.com and
  // owns its own header/footer. Suppress the legacy chrome so we don't
  // stack the old rainbow tagline banner on top of the redesigned home.
  const isSocialPulseNewHome = (() => {
    // Feb 2026 — the new SocialPulse chrome (SocialPulseHeader + Footer)
    // owns these routes on the parent domain. Suppress legacy chrome to
    // avoid the stacked-nav issue the Sept-2026 audit flagged.
    const SP_OWNED_PATHS = new Set([
      "/", "/about", "/founder", "/terms", "/topics", "/network", "/search",
      "/help", "/explore", "/contact", "/places", "/causes", "/community",
      "/join", "/press", "/careers", "/vision-mission", "/privacy",
      "/community-guidelines",
    ]);
    const SP_OWNED_PREFIXES = ["/pulse/", "/onboarding/", "/pulse-article/", "/admin/content-engine", "/admin/sisters", "/admin/ad-network", "/admin/ad-campaigns", "/ads/"];
    const isKnownOwnedPath =
      SP_OWNED_PATHS.has(pathname) ||
      SP_OWNED_PREFIXES.some((p) => pathname.startsWith(p));
    try {
      const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
      const site = (params.get("site") || "").toLowerCase();
      if (site === "socialpulse") return true;
      const KNOWN_SISTER_SLUGS = new Set([
        "kerala", "keralatravels", "indianfamilytree", "myfamilytreecare",
        "foodpulse", "sellnbuy", "socialcapital", "worldpulse", "indias",
        "usapulse", "blogspulse", "blog",
      ]);
      if (site && KNOWN_SISTER_SLUGS.has(site)) return false;
      const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
      if (!host) return isKnownOwnedPath;
      if (host.includes("indianfamilytree.com")) return false;
      if (host.includes("myfamilytreecare.com")) return false;
      if (host.includes("familytree.team")) return false;
      if (host.includes("keralatravels")) return false;
      if (host.includes("kerala.com")) return false;
      if (host.includes("civicpulse.blog")) return false;
      if (host.includes("socialpulse.blog")) return false;
      // Sep 2026 audit — on the SocialPulse parent domain we suppress
      // the legacy chrome for EVERY path so /join, /press, /careers,
      // /random-404 etc. never show the old locality bar / translate
      // strip / "Sign in / Join Social Pulse" header again. Any
      // unmatched path falls through to <SocialPulseNotFound /> which
      // uses the modern chrome.
      if (host.includes("socialpulse.com")) return true;
      return isKnownOwnedPath;
    } catch {
      return isKnownOwnedPath;
    }
  })();
  const suppressChrome = isHostedSite || isImmersive || isHeritageLanding || isMobileApp || isPublisherSurface || isBlogPreview || isSocialPulseNewHome;
  // Child-site trim — on foodpulse / kerala / IFT / MFT / indias / keralatravels
  // we hide the fat Social Pulse "Support / Portland card / Housing & Shelter
  // chips / Translate" strip so the child brand owns the viewport. The
  // slim header + LocalityHeader still render so navigation and locality
  // are preserved.
  const childSlug = (() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      // Preview stickiness: `?site=<slug>` is often stripped by internal
      // <Link>s during client-side navigation. Persist the resolved slug
      // for the lifetime of the tab so the child app doesn't unmount mid
      // session.
      if (typeof window !== "undefined") {
        if (q) {
          window.sessionStorage.setItem("np_site_slug", q);
        }
      }
      const stashed = typeof window !== "undefined"
        ? window.sessionStorage.getItem("np_site_slug")
        : null;
      const s = (resolveSiteSlug({ queryParam: q || stashed || "" }) || "").toLowerCase();
      return s && s !== "socialpulse" ? s : null;
    } catch { return null; }
  })();

  // TheNewsPulse.pro / newspulse.pro — completely different UI (editorial
  // masthead, no SocialPulse chrome). Short-circuit into its own router
  // before any of the SocialPulse chrome logic runs.
  if (childSlug === "newspulse") {
    return <NewsPulseApp />;
  }
  // BlogsPulse — SocialPulse.blog owns its own header/footer chrome.
  // Short-circuit before any of the global SocialPulse chrome logic runs.
  // Applies to /blog root, every /blog/* sub-page, AND to /auth + /login
  // (system-wide) so the beautiful blog-styled sign-in page is the ONLY
  // sign-in experience — no more falling back to the fat SocialPulse
  // chrome the user explicitly asked us to keep them away from.
  // Route the beautiful blog auth for `/auth` and `/login`, but never
  // for OAuth/SSO callback paths (they need the socialpulse chrome's
  // callback handler).
  const isSignInRoute = (pathname === "/auth" || pathname === "/login")
    && !pathname.startsWith("/auth/");
  const isBlogChrome = childSlug === "blogspulse" || pathname === "/blog" || pathname.startsWith("/blog/") || isSignInRoute;
  if (isBlogChrome) {
    return (
      <Routes>
        <Route path="/blog" element={<BlogHome />} />
        <Route path="/blog/news" element={<BlogNews />} />
        <Route path="/blog/places" element={<BlogPlaces />} />
        <Route path="/blog/topics" element={<BlogTopics />} />
        <Route path="/blog/topics/suggest" element={<BlogTopicSuggest />} />
        <Route path="/blog/topics/:slug" element={<BlogTopicHub />} />
        <Route path="/blog/questions" element={<BlogQuestions />} />
        <Route path="/blog/publications" element={<BlogPublications />} />
        <Route path="/blog/writers" element={<BlogWriters />} />
        <Route path="/blog/leaderboard" element={<BlogLeaderboard />} />
        <Route path="/blog/auth" element={<BlogAuth />} />
        <Route path="/auth" element={<BlogAuth />} />
        <Route path="/login" element={<BlogAuth />} />
        <Route path="/blog/:slug" element={<PublisherArticleView />} />
      </Routes>
    );
  }
  // CivicPulse — civicpulse.blog owns its own green civic chrome.
  // Short-circuit for the /civic apex + any nested route, and for the
  // `?site=civicpulse` (or civicpulse.blog hostname) preview override.
  const isCivicHost = childSlug === "civicpulse";
  const isCivicChrome = isCivicHost || pathname === "/civic" || pathname.startsWith("/civic/");
  if (isCivicChrome) {
    // On the canonical apex (civicpulse.blog) the routes live at the
    // root. On any other host they live under /civic/*. We render two
    // sets side-by-side so both hits resolve without duplicating the
    // component tree.
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAF7]">
        <CivicHeader />
        <main className="flex-1">
          <Routes>
            {/* Preview / cross-site path — always available. */}
            <Route path="/civic" element={<CivicHome />} />
            <Route path="/civic/report" element={<CivicReport />} />
            <Route path="/civic/map" element={<CivicMapPage />} />
            <Route path="/civic/resolved" element={<CivicResolved />} />
            <Route path="/civic/communities" element={<CivicCommunities />} />
            <Route path="/civic/authorities" element={<CivicAuthorityDirectory />} />
            <Route path="/civic/authorities/:id" element={<CivicAuthorityInbox />} />
            <Route path="/civic/issue/:publicId" element={<CivicIssueDetail />} />
            <Route path="/civic/:country" element={<CivicMapPage />} />
            <Route path="/civic/:country/:state" element={<CivicMapPage />} />
            <Route path="/civic/:country/:state/:district" element={<CivicMapPage />} />
            <Route path="/civic/:country/:state/:district/:city" element={<CivicMapPage />} />
            {/* Canonical civicpulse.blog apex — routes live at the root. */}
            {isCivicHost && (
              <>
                <Route path="/" element={<CivicHome />} />
                <Route path="/report" element={<CivicReport />} />
                <Route path="/map" element={<CivicMapPage />} />
                <Route path="/resolved" element={<CivicResolved />} />
                <Route path="/communities" element={<CivicCommunities />} />
                <Route path="/authorities" element={<CivicAuthorityDirectory />} />
                <Route path="/authorities/:id" element={<CivicAuthorityInbox />} />
                <Route path="/issue/:publicId" element={<CivicIssueDetail />} />
                <Route path="/:country" element={<CivicMapPage />} />
                <Route path="/:country/:state" element={<CivicMapPage />} />
                <Route path="/:country/:state/:district" element={<CivicMapPage />} />
                <Route path="/:country/:state/:district/:city" element={<CivicMapPage />} />
              </>
            )}
          </Routes>
        </main>
        <CivicFooter />
        <Toaster richColors position="top-center" />
      </div>
    );
  }
  // Cross-site FoodPulse routes (/basket, /pantry, /receipts, /food-pulse/*)
  // own their own cream/green branding, so we suppress the fat Social Pulse
  // chrome to avoid the visual mismatch flagged in the /basket audit.
  const isFoodPulseRoute = /^\/(food-pulse|basket|pantry|receipts|smart-basket)(\/|$)/.test(pathname);
  const isChildSite = Boolean(childSlug) || isFoodPulseRoute;
  // "Reader pages" — long-form articles / policies / static pages where
  // the reader is there to read. We drop non-essential chrome (locality
  // strip, translate bar) so ~90 px more content shows above the fold.
  // Header still renders so navigation is preserved.
  const isReaderPage =
    pathname === "/meet-the-founder" ||
    pathname === "/founder" ||
    pathname === "/about" ||
    pathname === "/vision-mission" ||
    pathname === "/social-capital" ||
    pathname === "/terms" ||
    pathname === "/terms-of-service" ||
    pathname === "/privacy" ||
    /^\/policies\//.test(pathname) ||
    /^\/legal\//.test(pathname);
  return (
    <>
      {/* SisterSitesBar self-guards to render only on IFT and MFTCare
          hosts — don't gate on `!isChildSite` here or IFT (which IS a
          child site) will never see its own sibling ribbon. */}
      {!suppressChrome && <SisterSitesBar />}
      {!suppressChrome && <Header />}
      <ScrollToContentOnRoute />
      {!suppressChrome && !isReaderPage && <VerificationBanner />}
      <IdleLogout />
      <DomSpellingLocaliser />
      {!isAuthPage && !suppressChrome && !isChildSite && !isReaderPage && <LocalityHeader />}
      {!isAuthPage && !suppressChrome && !isChildSite && isHomePage && <TaglineBanner />}
      {!isAuthPage && !suppressChrome && !isChildSite && isHomePage && <LocationHeroStrip />}
      {!isAuthPage && !suppressChrome && !isChildSite && isHomePage && <ExploreBar />}
      {/* Page translate bar — visible on every branded surface (SocialPulse,
          IFT, FoodPulse, etc.) so multilingual visitors can read every page
          in their own language. Translation is universally valuable and
          shouldn't be gated by child-site chrome suppression.
          On reader pages we drop the strip too since the header already
          exposes a language picker — the reader-focused pages don't need a
          second one competing for attention. */}
      {!isMobileApp && !isHostedSite && !isImmersive && !isSocialPulseNewHome && <PageTranslateBar />}
      {!isMobileApp && !isHostedSite && !isImmersive && !isSocialPulseNewHome && <PageTTSButton />}
      <GlobalInputTranslator />
      <main className="relative z-10">
        <Routes>
              {/* ========= Food Pulse Mobile Web App (/m/*) ========= */}
              <Route path="/m/splash" element={<MobileSplash />} />
              <Route path="/m/welcome/:step" element={<MobileOnboarding />} />
              <Route path="/m/login" element={<MobileLogin />} />
              <Route path="/m/add-recipe" element={<MobileAddRecipe />} />
              <Route path="/m/add-deal" element={<MobileAddDeal />} />
              <Route path="/m/recipes/:slug" element={<MobileRecipeDetail />} />
              <Route path="/m/products/:slug" element={<MobileProductDetail />} />
              <Route element={<MobileShell />}>
                <Route path="/m" element={<MobileHome />} />
                <Route path="/m/deals" element={<MobileDeals />} />
                <Route path="/m/settings" element={<MobileSettings />} />
                <Route path="/m/profile" element={<MobileProfile />} />
                <Route path="/m/news" element={<MobileNews />} />
                <Route path="/m/lists" element={<MobileMyLists />} />
                <Route path="/m/my-deals" element={<MobileMyDeals />} />
                <Route path="/m/recipes" element={<MobileRecipes />} />
                <Route path="/m/groups" element={<MobileGroups />} />
                <Route path="/m/products" element={<MobileProducts />} />
                <Route path="/m/stores" element={<MobileStores />} />
                <Route path="/m/charities" element={<MobileCharities />} />
                <Route path="/m/about" element={<MobileAbout />} />
              </Route>

              <Route path="/" element={<HomeRouter />} />
              <Route path="/advertise" element={<AdvertisePage />} />
              {/* Direct preview route — hittable on any host. */}
              <Route path="/blog" element={<BlogHome />} />
              <Route path="/blog/news" element={<BlogNews />} />
              <Route path="/blog/places" element={<BlogPlaces />} />
              <Route path="/blog/topics" element={<BlogTopics />} />
              <Route path="/blog/topics/suggest" element={<BlogTopicSuggest />} />
              <Route path="/blog/topics/:slug" element={<BlogTopicHub />} />
              <Route path="/blog/questions" element={<BlogQuestions />} />
              <Route path="/blog/publications" element={<BlogPublications />} />
              <Route path="/blog/writers" element={<BlogWriters />} />
              <Route path="/help" element={<HelpDispatcher />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/u/:handle" element={<PublicProfile />} />
              <Route path="/profile/edit" element={<ProfileEdit />} />
              <Route path="/nature" element={<NatureHome />} />
              <Route path="/nature/welcome" element={<NatureWelcome />} />
              <Route path="/nature/from-the-founder" element={<NatureFromTheFounder />} />
              <Route path="/nature/founder" element={<NatureFromTheFounder />} />
              <Route path="/local" element={<SellnBuyHome />} />
              <Route path="/sellnbuy" element={<SellnBuyHome />} />
              <Route path="/local/listings/:id" element={<LocalListingDetail />} />
              <Route path="/local/browse/:categorySlug" element={<LocalCategoryBrowse />} />
              {/* SocialCapital.cc — trust & reputation ledger */}
              <Route path="/sc" element={<SocialCapitalHome />} />
              <Route path="/socialcapital" element={<SocialCapitalHome />} />
              <Route path="/sc/me" element={<SocialCapitalPassport selfMode />} />
              <Route path="/sc/passport/:scid" element={<SocialCapitalPassport />} />
              <Route path="/sc/org" element={<SocialCapitalOrgDashboard />} />
              <Route path="/sc/docs" element={<SocialCapitalApiDocs />} />
              <Route path="/sc/:slug" element={<SocialCapitalArticle />} />
              <Route path="/nature/identify" element={<NatureIdentify />} />
              <Route path="/nature/observations" element={<NatureObservations />} />
              <Route path="/nature/atlas/:category" element={<NatureAtlasCategory />} />
              <Route path="/nature/species/:slug" element={<NatureSpeciesProfile />} />
              <Route path="/nature/legacy" element={<Nature />} />
              <Route path="/nature/discover" element={<NatureDiscover />} />
              <Route path="/weather" element={<WeatherAlmanac />} />
              <Route path="/weather/about" element={<WeatherAlmanacAbout />} />
              <Route path="/weather/topic/:key" element={<WeatherTopicPage />} />
              <Route path="/topic/:key" element={<GenericTopicPage />} />
              <Route path="/weather/community" element={<WeatherCommunity />} />
              <Route path="/weather/report/:id" element={<WeatherReportDetail />} />
              <Route path="/weather/farmer" element={<FarmerDashboard />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/marketplace/new" element={<MarketplaceNew />} />
              <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
              {/* --- Business Portal (Phase 2 framework) --- */}
              <Route path="/businesses" element={<BusinessPortalHome />} />
              <Route path="/businesses/search" element={<BusinessPortalHome />} />
              <Route path="/businesses/new" element={<NewBusinessPage />} />
              <Route path="/businesses/in/:location" element={<BusinessLocationPage />} />
              <Route path="/business/:slug/edit" element={<BusinessEditPage />} />
              <Route path="/business/:slug/products/new" element={<ProductEditPage />} />
              <Route path="/business/:slug/products/:productId/edit" element={<ProductEditPage />} />
              <Route path="/business/:slug" element={<BusinessProfilePage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/jobs" element={<JobsHome />} />
              <Route path="/jobs/dashboard" element={<JobsDashboardPage />} />
              <Route path="/jobs/my" element={<JobsDashboardPage />} />
              <Route path="/jobs/post" element={<JobsPostPage />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage />} />
              <Route path="/realestate" element={<RealEstateHome />} />
              <Route path="/realestate/post" element={<RealEstatePostPage />} />
              <Route path="/realestate/:propId" element={<PropertyDetailPage />} />
              <Route path="/travel" element={<TravelHome />} />
              <Route path="/travel/operators/new" element={<TravelOperatorNew />} />
              <Route path="/travel/:type/:slug" element={<TravelDetailPage />} />
              <Route path="/travel/:type" element={<TravelListPage />} />
              <Route path="/quotes/submit" element={<QuoteSubmitPage />} />
              <Route path="/quotes/authors/:slug" element={<QuoteAuthorPage />} />
              <Route path="/quotes/:slug" element={<QuoteDetailPage />} />
              <Route path="/admin/quotes/import" element={<QuoteAdminImport />} />
              <Route path="/admin/articles" element={<AdminArticleList />} />
              <Route path="/admin/articles/edit/:slug" element={<AdminArticleEditor />} />
              <Route path="/admin/travel/generate" element={<TravelAdminGenerate />} />
              <Route path="/admin/photos" element={<AdminPhotosReview />} />
              <Route path="/admin/ads" element={<AdminAdsManager />} />
              <Route path="/admin/moderation/feedback" element={<AdminModerationFeedback />} />
              <Route path="/staff/magic-link" element={<StaffMagicLinkConsume />} />
              {/* --- Sports & Games --- */}
              <Route path="/sports" element={<SportsHome />} />
              <Route path="/sports/categories" element={<SportsCategoryPage />} />
              <Route path="/sports/tournaments" element={<SportsTournamentsList />} />
              <Route path="/sports/tournaments/new" element={<SportsTournamentNew />} />
              <Route path="/sports/tournaments/:slug" element={<SportsTournamentDetail />} />
              <Route path="/sports/clubs" element={<SportsClubsList />} />
              <Route path="/sports/clubs/new" element={<SportsClubNew />} />
              <Route path="/sports/clubs/:slug" element={<SportsClubDetail />} />
              {/* --- Education --- */}
              <Route path="/education" element={<EducationHome />} />
              <Route path="/education/institutions" element={<EducationInstitutionsList />} />
              <Route path="/education/institutions/new" element={<EducationInstitutionNew />} />
              <Route path="/education/institutions/:slug" element={<EducationInstitutionDetail />} />
              <Route path="/education/scholarships" element={<EducationScholarshipsList />} />
              <Route path="/education/scholarships/new" element={<EducationScholarshipNew />} />
              <Route path="/education/scholarships/:slug" element={<EducationScholarshipDetail />} />
              {/* --- Instant Websites --- */}
              <Route path="/websites/new" element={<WebsiteWizard />} />
              <Route path="/my/websites" element={<MyWebsitesPage />} />
              <Route path="/websites/:id/edit" element={<WebsiteEditPage />} />
              <Route path="/sites/:slug" element={<PublicWebsitePage />} />
              {/* --- Government --- */}
              <Route path="/government" element={<GovernmentHome />} />
              <Route path="/government/departments" element={<GovernmentDepartmentsList />} />
              <Route path="/government/departments/new" element={<GovernmentDepartmentNew />} />
              <Route path="/government/departments/:slug" element={<GovernmentDepartmentDetail />} />
              <Route path="/government/notices" element={<GovernmentNoticesList />} />
              <Route path="/government/my-messages" element={<MyGovernmentMessagesPage />} />
              {/* --- Health, Wellness & Medicine --- */}
              <Route path="/health" element={<HealthHome />} />
              <Route path="/health/hospitals"       element={<HealthFacilitiesList facilityType="hospital"   label="Hospitals & Medical Centres" />} />
              <Route path="/health/clinics"         element={<HealthFacilitiesList facilityType="clinic"     label="Clinics" />} />
              <Route path="/health/pharmacies"      element={<HealthFacilitiesList facilityType="pharmacy"   label="Pharmacies" />} />
              <Route path="/health/laboratories"    element={<HealthFacilitiesList facilityType="laboratory" label="Medical Laboratories" />} />
              <Route path="/health/imaging"         element={<HealthFacilitiesList facilityType="imaging"    label="Imaging Centres" />} />
              <Route path="/health/wellness-centres" element={<HealthFacilitiesList facilityType="wellness"  label="Wellness Centres" />} />
              <Route path="/health/facilities/:slug" element={<HealthFacilityDetail />} />
              <Route path="/health/providers"       element={<HealthProvidersList />} />
              <Route path="/health/providers/:slug" element={<HealthProviderDetail />} />
              <Route path="/health/diseases"    element={<HealthTopicsList kind="disease"     label="Diseases & Conditions" description="Educational articles on common and rare diseases. AI-written on first visit under strict editorial safety rules."/>} />
              <Route path="/health/symptoms"    element={<HealthTopicsList kind="symptom"     label="Symptoms Guide"        description="Understand what a symptom may mean and when to seek care."/>} />
              <Route path="/health/procedures"  element={<HealthTopicsList kind="procedure"   label="Medical Procedures"    description="Learn what to expect before, during and after common procedures."/>} />
              <Route path="/health/drugs"       element={<HealthTopicsList kind="drug"        label="Drug Information"      description="General information about medications. Not a substitute for a doctor's prescription."/>} />
              <Route path="/health/wellness"    element={<HealthTopicsList kind="wellness"    label="Wellness"              description="Nutrition, sleep, movement and everyday habits for a healthier life."/>} />
              <Route path="/health/mental-health"        element={<HealthTopicsList kind="mental"      label="Mental Health"         description="Signs, self-care and when to reach out for professional support."/>} />
              <Route path="/health/traditional-medicine" element={<HealthTopicsList kind="traditional" label="Traditional Medicine"  description="Balanced, evidence-aware guides to Ayurveda, TCM, Homeopathy and more."/>} />
              <Route path="/health/articles"    element={<HealthTopicsList kind="disease"     label="Health Articles"       description="All health articles across the platform."/>} />
              <Route path="/health/topics/:kind/:name" element={<HealthTopicDetail />} />
              {/* --- Arts, Music & Cinema --- */}
              <Route path="/arts" element={<ArtsHome />} />
              <Route path="/arts/submit" element={<ArtsSubmit />} />
              <Route path="/arts/awards" element={<ArtsAwardsList />} />
              <Route path="/arts/awards/:slug" element={<ArtsAwardDetail />} />
              <Route path="/arts/marketplace" element={<ArtsMarketplace />} />
              <Route path="/arts/:section" element={<ArtsSection />} />
              <Route path="/arts/:section/:slug" element={<ArtsProfile />} />
              {/* --- Global Knowledge Graph --- */}
              <Route path="/explore"              element={<ExploreDispatcher />} />
              <Route path="/person/:slug"         element={<KgEntityDetail entityType="person" />} />
              <Route path="/place/:slug"          element={<KgEntityDetail entityType="place" />} />
              <Route path="/organization/:slug"   element={<KgEntityDetail entityType="organization" />} />
              <Route path="/event/:slug"          element={<KgEntityDetail entityType="event" />} />
              <Route path="/disease/:slug"        element={<KgEntityDetail entityType="disease" />} />
              <Route path="/kg/:entityType/:name" element={<KgEntityDetail />} />
              <Route path="/civic" element={<Civic />} />
              <Route path="/pulse" element={<NeighborhoodPulse />} />
              <Route path="/pulse-item/:id" element={<PulseItemDetail />} />
              <Route path="/pulse/*" element={<NeighborhoodDetail />} />
              <Route path="/my-content" element={<MyContent />} />
              <Route path="/articles" element={<ArticlesList />} />
              <Route path="/articles/new" element={<ArticleCompose />} />
              <Route path="/articles/:slug/edit" element={<ArticleCompose />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/find-help" element={<FindHelp />} />
              <Route path="/request-help" element={<RequestHelp />} />
              <Route path="/offer-help" element={<OfferHelp />} />
              <Route path="/offer-help/:slug" element={<AppealDetail />} />
              <Route path="/organizations" element={<Organizations />} />
              <Route path="/organisations" element={<Organizations />} />
              <Route path="/topics"        element={<TopicsIndex />} />
              <Route path="/organizations/compare" element={<OrganizationsCompare />} />
              <Route path="/members" element={<Members />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/profile/resume-studio" element={<ResumeStudio />} />
              <Route path="/profile/resume-builder" element={<ResumeBuilder />} />
              <Route path="/resume/builder" element={<ResumeBuilder />} />
              <Route path="/family/:treeSlug/member/:memberId" element={<MemberProfile />} />
              <Route path="/family/:treeSlug/member/:memberId/edit" element={<MemberEdit />} />
              <Route path="/organizations/:id" element={<OrganizationDetail />} />
              <Route path="/housing" element={<Housing />} />
              <Route path="/housing/habitat-for-humanity" element={<HabitatForHumanity />} />
              <Route path="/housing/:id" element={<HousingDetail />} />
              <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/volunteer" element={<VolunteerHome />} />
              <Route path="/volunteer/benefits/:slug" element={<VolunteerBenefit />} />
              <Route path="/stories" element={<Stories />} />
              <Route path="/stories/:slug" element={<StoryDetail />} />
              <Route path="/change-makers" element={<WorldChangers />} />
              <Route path="/change-makers/:slug" element={<WorldChangerDetail />} />
              <Route path="/world-changers" element={<WorldChangers />} />
              <Route path="/world-changers/:slug" element={<WorldChangerDetail />} />
              <Route path="/categories/:slug" element={<CategoryArticle />} />
              <Route path="/vision-mission" element={<VisionMission />} />
              <Route path="/social-capital" element={<SocialCapitalPage />} />
              <Route path="/food-pulse" element={<FoodPulseHome />} />
              <Route path="/publisher/write" element={<PublisherWrite />} />
              <Route path="/publisher/write/:articleId" element={<PublisherWrite />} />
              <Route path="/publisher/dashboard" element={<PublisherDashboard />} />
              <Route path="/writers/:writerId" element={<WriterProfile />} />
              <Route path="/publisher/admin" element={<PublisherAdmin />} />
              <Route path="/publisher/photopulse-queue" element={<PhotoPulseQueue />} />
              <Route path="/publisher/notes" element={<PublisherNotesFeed />} />
              <Route path="/article/:slug" element={<PublisherArticleView />} />
              {/* SocialPulse.blog canonical article URL — same reader,
                  keeps the .blog wordmark in the URL bar. */}
              <Route path="/blog/:slug" element={<PublisherArticleView />} />
              <Route path="/chat" element={<ChatPulseHome />} />
              <Route path="/publisher/place/:slug" element={<PublisherHub kind="place" />} />
              <Route path="/publisher/topic/*" element={<PublisherHub kind="topic" />} />
              <Route path="/publisher/author/:authorId" element={<PublisherHub kind="author" />} />
              {/* Legacy alias: /publisher/@:id → /publisher/author/:id (component parses the @) */}
              <Route path="/publisher/*" element={<PublisherHubLegacyRedirect />} />
              <Route path="/what-is-food-pulse" element={<WhatIsFoodPulse />} />
              <Route path="/impact-map" element={<ImpactMap />} />
              <Route path="/contact" element={<ContactDispatcher />} />
              <Route path="/community" element={<CommunityDispatcher />} />
              <Route path="/donate" element={<Donate />} />
              <Route path="/donate/success" element={<DonateSuccess />} />
              <Route path="/donate/cancel" element={<DonateCancel />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/l/*" element={<LocationArticle />} />
              <Route path="/lr/*" element={<LinkReferencePage />} />
              <Route path="/guides/*" element={<GuidesConsole />} />
              <Route path="/stamps" element={<StampGallery />} />
              <Route path="/bookmarks" element={<BookmarksDashboard />} />
              <Route path="/directory/:kind" element={<KindDirectoryPage />} />
              <Route path="/kerala" element={<KeralaHome />} />
              <Route path="/travels" element={<KeralaTravelsHome />} />
              <Route path="/lt/*" element={<LocationTopicPage />} />
              <Route path="/places/detail/*" element={<PoiDetail />} />
              <Route path="/places" element={<PlacesDispatcher />} />
              <Route path="/places/*" element={<PlacesDirectory />} />
              <Route path="/pricing" element={<PricingDispatcher />} />
              <Route path="/premium/success" element={<PremiumSuccess />} />
              <Route path="/premium/cancel" element={<PremiumCancel />} />
              <Route path="/causes" element={<CausesDispatcher />} />
              <Route path="/causes/:id" element={<CauseDetail />} />
              <Route path="/submit-cause" element={<SubmitCause />} />
              <Route path="/celebrate" element={<Celebrate />} />
              {/* IFT-branded aliases — preserved so the marketing-landing links
                  from indianfamilytree.com continue to resolve inside the SPA. */}
              <Route path="/celebrate-a-life" element={<Celebrate />} />
              <Route path="/famous-families" element={<IftFamousFamilies />} />
              <Route path="/indian-heritage" element={<IftIndianHeritage />} />
              <Route path="/indian-heritage/:slug" element={<HeritageArticlePage />} />
              <Route path="/unsplash-demo" element={<UnsplashComplianceDemo />} />
              <Route path="/explore-india" element={<IftExploreIndia />} />
              <Route path="/about"         element={<SocialPulseAbout />} />
              <Route path="/founder"       element={<SocialPulseFounder />} />
              {/* SocialPulse parent-domain owned pages — routed BEFORE
                  the /:handle catch-all so they never get shadowed by
                  the profile router. */}
              <Route path="/join"                  element={<SocialPulseJoin />} />
              <Route path="/press"                 element={<SocialPulsePress />} />
              <Route path="/careers"               element={<SocialPulseCareers />} />
              <Route path="/vision-mission"        element={<SocialPulseVisionMission />} />
              <Route path="/privacy"               element={<SocialPulsePrivacy />} />
              <Route path="/community-guidelines"  element={<SocialPulseCommunityGuidelines />} />
              <Route path="/topics"                element={<SocialPulseTopics />} />
              {/* AI Content Engine — public + admin routes */}
              <Route path="/pulse-article/:slug"   element={<PulseArticle />} />
              <Route path="/admin/content-engine/report" element={<AdminContentEngineReport />} />
              <Route path="/admin/content-engine"  element={<AdminContentEngine />} />
              <Route path="/admin/sisters"          element={<AdminSisters />} />
              <Route path="/admin/ad-network"       element={<AdminAds />} />
              <Route path="/admin/ad-campaigns"     element={<AdminAdCampaigns />} />
              <Route path="/admin/ad-campaigns/:cid" element={<AdminAdCampaignAnalytics />} />
              <Route path="/ads/new"                element={<AdBuilder />} />
              <Route path="/about-ift"     element={<IftAboutPage />} />

              {/* Cross-site preview link — lets visitors on socialpulse.com
                  jump directly to the IFT experience without needing to
                  visit indianfamilytree.com or juggle a query string. */}
              <Route path="/indian-family-tree" element={<IndianFamilyTreeHome />} />
              <Route path="/ift"                element={<Navigate to="/indian-family-tree" replace />} />

              {/* Food Pulse — Global Food Knowledge Database (Phase 1) */}
              <Route path="/food-pulse/food" element={<FoodItemsDirectory />} />
              <Route path="/food-pulse/food/:slug" element={<FoodItemPage />} />
              <Route path="/food-pulse/businesses" element={<FoodBusinessesDirectory />} />
              <Route path="/food-pulse/businesses/:slug" element={<FoodBusinessPage />} />
              <Route path="/food-pulse/products" element={<FoodPulseProductsDirectory />} />
              <Route path="/food-pulse/products/:productId" element={<FoodPulseProductDetail />} />
              <Route path="/food-pulse/urban-harvest" element={<FoodPulseUrbanHarvest />} />
              <Route path="/food-pulse/urban-harvest/story" element={<FoodPulseUrbanHarvestStory />} />
              <Route path="/food-pulse/urban-harvest/pick-your-own"        element={<FoodPulseUrbanHarvestPickYourOwn />} />
              <Route path="/food-pulse/urban-harvest/pre-picked-curbside"  element={<FoodPulseUrbanHarvestPrePickedCurbside />} />
              <Route path="/food-pulse/urban-harvest/donation-only"        element={<FoodPulseUrbanHarvestDonationOnly />} />
              <Route path="/food-pulse/urban-harvest/harvest-help-needed"  element={<FoodPulseUrbanHarvestHarvestHelp />} />
              <Route path="/food-pulse/articles" element={<FoodPulseArticles />} />
              <Route path="/food-pulse/articles/:slug" element={<FoodPulseArticlePage />} />
              {/* Mother-site aliases — surface the same Food Pulse articles
                  under SocialPulse chrome so they're discoverable via the
                  main nav's "Food" entry. Reuses the same components; the
                  branding difference comes from the surrounding Header
                  which is hostname/query-aware. */}
              <Route path="/food" element={<FoodPulseArticles />} />
              <Route path="/food/:slug" element={<FoodPulseArticlePage />} />
              {/* Generic FoodPulse section landing pages — reads from
                  the FP_SECTIONS manifest so About / Recipes / Health /
                  Deals / Community / Farming / Places / News /
                  Marketplace all render a hero + child-tile grid without
                  hand-building 100 pages. */}
              <Route path="/food-pulse/about" element={<FoodPulseAbout />} />
              <Route path="/food-pulse/admin" element={<FoodPulseAdmin />} />
              <Route path="/food-pulse/legal/terms" element={<FoodPulseTerms />} />
              <Route path="/food-pulse/legal/privacy" element={<Navigate to="/policies/foodpulse-privacy-policy" replace />} />
              <Route path="/food-pulse/legal/cookies" element={<Navigate to="/policies/foodpulse-cookie-policy" replace />} />
              {/* India DPDP privacy — linked from the login DPDP consent tickbox */}
              <Route path="/legal/india-privacy" element={<IndiaPrivacyPolicy />} />
              <Route path="/settings/nominee" element={<NomineeSettings />} />
              <Route path="/nominee/accept/:token" element={<NomineeAccept />} />
              <Route path="/food-pulse/health" element={<FoodPulseHealth />} />
              <Route path="/food-pulse/health/:topic" element={<FoodPulseHealth />} />
              <Route path="/food-pulse/recipes/:hubSlug" element={<FoodPulseRecipesCategory />} />
              {/* Deal-of-the-Day flow */}
              <Route path="/food-pulse/deals" element={<FoodPulseDealsGrid />} />
              <Route path="/food-pulse/deals/:slug" element={<FoodPulseDealDetail />} />
              <Route path="/food-pulse/vendor" element={<VendorDashboard />} />
              <Route path="/food-pulse/vendor/import" element={<CsvDealImport />} />
              <Route path="/food-pulse/my-lists" element={<MyFoodLists />} />
              <Route path="/food-pulse/lists/wishlist/new" element={<AddWishProductForm />} />
              <Route path="/food-pulse/receipts/new" element={<ReceiptScanner />} />
              <Route path="/food-pulse/receipts/mine" element={<MyReceipts />} />
              <Route path="/food-pulse/household" element={<HouseholdDashboard />} />
              <Route path="/food-pulse/basket" element={<SmartBasket />} />
              {/* ---- Cross-site aliases: same components, work from
                       any site (SocialPulse / IFT / FoodPulse). ------- */}
              <Route path="/receipts/new"           element={<ReceiptScanner />} />
              <Route path="/receipts/mine"          element={<MyReceipts />} />
              <Route path="/household"              element={<HouseholdDashboard />} />
              <Route path="/basket"                 element={<SmartBasket />} />
              <Route path="/my-lists"               element={<MyFoodLists />} />
              <Route path="/lists/wishlist/new"     element={<AddWishProductForm />} />
              <Route path="/food-pulse/spot" element={<SpotADeal />} />
              <Route path="/food-pulse/harvest" element={<HarvestBoard />} />
              <Route path="/food-pulse/markets" element={<FarmersMarkets />} />
              <Route path="/food-pulse/farm/edit/:slug" element={<FarmProfileEditor />} />
              <Route path="/food-pulse/farm/:slug" element={<FarmstandPage />} />
              <Route path="/food-pulse/encyclopedia" element={<IngredientEncyclopedia />} />
              <Route path="/food-pulse/encyclopedia/:slug" element={<IngredientDetail />} />
              <Route path="/food-pulse/label-scan" element={<FoodPulseLabelScan />} />
              {/* Topic landings — one intro article per dropdown item */}
              <Route path="/food-pulse/community"               element={<FoodPulseTopicLanding section="community" />} />
              <Route path="/food-pulse/community/:topic"        element={<FoodPulseTopicLanding section="community" />} />
              <Route path="/food-pulse/deals-guide"             element={<FoodPulseTopicLanding section="deals-guide" />} />
              <Route path="/food-pulse/deals-guide/:topic"      element={<FoodPulseTopicLanding section="deals-guide" />} />
              <Route path="/food-pulse/news"                    element={<FoodPulseTopicLanding section="news" />} />
              <Route path="/food-pulse/news/:topic"             element={<FoodPulseTopicLanding section="news" />} />
              <Route path="/food-pulse/food-waste-guide"        element={<FoodPulseTopicLanding section="food-waste" />} />
              <Route path="/food-pulse/food-waste-guide/:topic" element={<FoodPulseTopicLanding section="food-waste" />} />
              <Route path="/food-pulse/discover"                element={<FoodPulseTopicLanding section="discover" />} />
              <Route path="/food-pulse/discover/:topic"         element={<FoodPulseTopicLanding section="discover" />} />
              <Route path="/food-pulse/farming"                 element={<FoodPulseTopicLanding section="farming" />} />
              <Route path="/food-pulse/farming/:topic"          element={<FoodPulseTopicLanding section="farming" />} />
              <Route path="/food-pulse/marketplace"              element={<FoodPulseTopicLanding section="marketplace" />} />
              <Route path="/food-pulse/marketplace/:topic"       element={<FoodPulseTopicLanding section="marketplace" />} />
              <Route path="/food-pulse/places"                   element={<FoodPulseTopicLanding section="places" />} />
              <Route path="/food-pulse/places/:topic"            element={<FoodPulseTopicLanding section="places" />} />
              <Route path="/food-pulse/:section" element={<FoodPulseSection />} />
              <Route path="/admin/food-items" element={<AdminFoodItems />} />
              {/* Cemeteries Directory — cross-border listing of burial grounds.
                  Powers the /cemeteries wing on the MyFamilyTree.care landing. */}
              <Route path="/cemeteries" element={<CemeteriesDirectory />} />
              <Route path="/cemeteries/:slug" element={<CemeteryDetail />} />
              {/* Funeral Services Directory — funeral homes, priests, transport,
                  repatriation, monument makers etc. Surfaces under Obituaries,
                  Memorials and Family Tree navigation on all three sibling sites. */}
              <Route path="/funeral-services" element={<FuneralServicesDirectory />} />
              <Route path="/funeral-services/:slug" element={<FuneralServiceDetail />} />
              {/* Bridge — MyFamilyTree.care landing links here. */}
              <Route path="/category/:slug" element={<CategoryRedirect />} />
              <Route path="/celebrate/browse" element={<CelebrateBrowse />} />
              <Route path="/obituaries" element={<Obituaries />} />
              <Route path="/obituaries/hub" element={<ObituariesHub />} />
              <Route path="/obituaries/category/:category" element={<ObituariesCategory />} />
              <Route path="/obituaries/submit" element={<ObituariesSubmit />} />
              <Route path="/pricing/success" element={<PricingSuccess />} />
              <Route path="/pricing/cancel" element={<PricingCancel />} />
              {/* IFT Terms of Service — /terms alias for the policy renderer */}
              <Route path="/terms" element={<SocialPulseTerms />} />
              <Route path="/terms-ift" element={<Navigate to="/policies/ift-terms-of-service" replace />} />
              <Route path="/terms-of-service" element={<Navigate to="/policies/ift-terms-of-service" replace />} />
              <Route path="/privacy" element={<Navigate to="/policies/ift-privacy-policy" replace />} />
              <Route path="/family-tree-guide" element={<FamilyTreeGuide />} />
              <Route path="/celebrate/submit" element={<CelebrateSubmit />} />
              <Route path="/celebrate/link-existing" element={<CelebrateLinkExisting />} />
              <Route path="/celebrate/:slug/announcement-print" element={<CelebrateAnnouncementPrint />} />
              <Route path="/celebrate/:slug" element={<CelebrateDetail />} />
              <Route path="/things-to-know" element={<ThingsToKnow />} />
              <Route path="/search" element={<SearchDispatcher />} />
              <Route path="/network" element={<SocialPulseNetwork />} />
              <Route path="/family" element={<FamilyTreesList />} />
              <Route path="/family-trees" element={<FamilyTreesList />} />
              <Route path="/family-trees/new" element={<FamilyTreeNew />} />
              <Route path="/family/:treeId" element={<FamilyTreeDetail />} />
              <Route path="/family-and-memorials" element={<HeritageHome />} />
              <Route path="/heritage" element={<HeritageHome />} />
              <Route path="/global-family-trees" element={<GlobalFamilyTrees />} />
              <Route path="/family-and-memorials/legacy" element={<FamilyMemorialsMarketing />} />
              <Route path="/family/:treeId/manage" element={<FamilyManagementCenter />} />
              <Route path="/family/:treeSlug/book/:docId" element={<FamilyBookViewer />} />
              <Route path="/state/:stateSlug" element={<IndianFamilyTreeStatePage />} />
              <Route path="/kin-network" element={<KinNetwork />} />
              <Route path="/family/:treeId/photos-batch" element={<FamilyPhotosBatch />} />
              <Route path="/almanac/records/:slug" element={<AlmanacRecordDetail />} />
              <Route path="/almanac/map" element={<AlmanacMap />} />
              <Route path="/admin/almanac/new" element={<AlmanacAdminNew />} />
              <Route path="/family/:treeId/mixer" element={<ReunionMixer />} />
              <Route path="/person/:spId" element={<PersonProfile />} />
              <Route path="/things-to-know/submit" element={<NewsSubmit />} />
              <Route path="/things-to-know/archive" element={<ThingsToKnowArchive />} />
              <Route path="/things-to-know/:id" element={<ThingsToKnowDetail />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/recipes/shopping-list" element={<ShoppingList />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/submit" element={<ServiceSubmit />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/recipes/submit" element={<RecipeSubmit />} />
              <Route path="/recipes/:slug/print" element={<RecipePrint />} />
              <Route path="/recipes/:slug" element={<RecipeDetail />} />
              <Route path="/in-memoriam" element={<InMemoriam />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/submit" element={<EventSubmit />} />
              <Route path="/events/:slug" element={<EventDetail />} />
              <Route path="/policies/:slug" element={<PolicyPage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/me" element={<MyProfile />} />
              <Route path="/pulse/:handle" element={<SocialPulseProfile />} />
              <Route path="/onboarding/profile" element={<SocialPulseProfileOnboard />} />
              <Route path="/profile" element={<MyProfile />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/contact-requests" element={<AdminContactInbox />} />
              <Route path="/admin/contact" element={<AdminContactFormInbox />} />
              <Route path="/admin/obituary-import" element={<AdminObituaryImport />} />
              <Route path="/admin/ai-topics/photos" element={<AdminAiTopicPhotos />} />
              <Route path="/admin/sweeps" element={<AdminSweepProgress />} />
              <Route path="/explore/india" element={<ExploreIndiaLanding />} />
              <Route path="/explore/usa" element={<ExploreUsaLanding />} />
              <Route path="/admin/location/*" element={<AdminLocationEditor />} />
              <Route path="/admin/satellites" element={<AdminSatellites />} />
              <Route path="/admin/network/launch/:slug" element={<AdminNetworkLaunch />} />
              <Route path="/daily" element={<DailyPulse />} />
              <Route path="/daily/:city" element={<DailyPulse />} />
              <Route path="/daily/:city/:date" element={<DailyPulse />} />
              <Route path="/meet-the-founder" element={<MeetTheFounder />} />
              <Route path="/founder" element={<Navigate to="/meet-the-founder" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
              {/* SocialPulse Services™ — centralized lead/request marketplace */}
              <Route path="/hire" element={<ServicesHomePage />} />
              <Route path="/hire/request" element={<RequestServicePage />} />
              <Route path="/hire/rfs/:id" element={<RFSDetailPage />} />
              <Route path="/hire/provider" element={<ProviderDashboardPage />} />
              <Route path="/admin/intelligence" element={<IntelligenceAdminPage />} />
              <Route path="/admin/sso"          element={<AdminSsoRolloutPage />} />
              <Route path="/admin/email-outbox" element={<AdminEmailOutbox />} />
              <Route path="/admin/social-capital" element={<AdminSocialCapitalLedger />} />
              {/* Bare-handle member page — MUST stay just above the
                    NotFound catch-all so it doesn't shadow other routes.
                    Backend guards against reserved words. */}
              <Route path="/:handle/resume" element={<PublicResumePage />} />
              <Route path="/wp" element={<WorldPulseHome />} />
              <Route path="/wp/*" element={<LocationPortalPage />} />
              <Route path="/:handle" element={<MemberProfilePage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          {!isHostedSite && !isImmersive && !isMobileApp && <Footer />}
          {!isMobileApp && !isSocialPulseNewHome && <AIHelper />}
          {!isMobileApp && !isSocialPulseNewHome && <ReportContributionFAB />}
          {!isMobileApp && !isSocialPulseNewHome && <QuickReporterButton variant="floating" />}
          <CountryAutoDetect />
          <ContentCmsShortcut />
          {!isMobileApp && !isSocialPulseNewHome && <CookieBanner />}
          {!isMobileApp && !isSocialPulseNewHome && <LanguageAutoSuggest />}
          {!isMobileApp && !isSocialPulseNewHome && <FontScaleSwitcher />}
          <Toaster richColors position="top-center" />
    </>
  );
}

export default App;
